/**
 * Device-agent inventory model (Vigía P-6), a TS mirror of
 * Rust's `native-agent::inventory::Inventory`. The browser agent
 * (`collector.ts`) fills in what the web APIs expose; the fields not
 * available are left empty (honest "partial" collection).
 */

import { isRecord } from "../p2p/guards";

export interface CpuInfo {
  model: string;
  arch: string;
  physical_cores: number;
  logical_cores: number;
  max_freq_mhz: number;
}

export interface MemoryInfo {
  total_bytes: number;
  available_bytes: number;
}

export interface DiskInfo {
  name: string;
  mount_point: string;
  filesystem: string;
  total_bytes: number;
  available_bytes: number;
}

export interface OsInfo {
  name: string;
  version: string;
  kernel: string;
  hostname: string;
  arch: string;
}

export interface NetInterface {
  name: string;
  mac: string | null;
  up: boolean;
}

export interface TemperatureReading {
  sensor: string;
  celsius: number;
}

export interface Inventory {
  device_id: string;
  collected_at: string;
  os: OsInfo;
  cpu: CpuInfo;
  memory: MemoryInfo;
  disks: DiskInfo[];
  gpus: { model: string; vram_bytes: number; driver: string | null }[];
  peripherals: { kind: string; name: string; connection: string }[];
  packages: { name: string; version: string; source: string }[];
  interfaces: NetInterface[];
  temperatures: TemperatureReading[];
}

/** A sealed device report (immutable version of the inventory). */
export interface SealedPart {
  device_id: string;
  version: number;
  collected_at: string;
  inventory: Inventory;
  seal: string;
}

/** Minimal structural type guard for the inventory (untrusted JSON from the DB). */
export function isInventory(value: unknown): value is Inventory {
  return (
    isRecord(value) &&
    typeof value.device_id === "string" &&
    typeof value.collected_at === "string" &&
    isRecord(value.os) &&
    isRecord(value.cpu) &&
    isRecord(value.memory) &&
    Array.isArray(value.disks) &&
    Array.isArray(value.interfaces) &&
    Array.isArray(value.temperatures)
  );
}

/**
 * Parses the JSON of a persisted report into `Inventory`, or `null` if the text is
 * invalid/corrupt (never throws — the UI degrades to "no inventory").
 */
export function parseInventory(raw: string | null | undefined): Inventory | null {
  if (!raw) {
    return null;
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  return isInventory(value) ? value : null;
}

export type AlertSeverity = "info" | "warning" | "critical";

export interface FleetAlert {
  rule_id: string;
  severity: AlertSeverity;
  metric: string;
  value: number;
  threshold: number;
  message: string;
}

const PERCENT = 100;

/** % usage of the fullest disk (or null if there are no disks). */
export function worstDiskUsedPercent(inv: Inventory): number | null {
  let worst: number | null = null;
  for (const d of inv.disks) {
    if (d.total_bytes <= 0) {
      continue;
    }
    const used = ((d.total_bytes - d.available_bytes) / d.total_bytes) * PERCENT;
    worst = worst === null ? used : Math.max(worst, used);
  }
  return worst;
}

/** % memory usage. */
export function memoryUsedPercent(inv: Inventory): number | null {
  if (inv.memory.total_bytes <= 0) {
    return null;
  }
  return ((inv.memory.total_bytes - inv.memory.available_bytes) / inv.memory.total_bytes) * PERCENT;
}

/** Maximum observed temperature. */
export function maxTemperatureC(inv: Inventory): number | null {
  let max: number | null = null;
  for (const t of inv.temperatures) {
    max = max === null ? t.celsius : Math.max(max, t.celsius);
  }
  return max;
}
